import { ActivatedRoute, Router } from '@angular/router'
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { LiveAnnouncer } from '@angular/cdk/a11y'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatSort, Sort } from '@angular/material/sort'
import { MatTable, MatTableDataSource } from '@angular/material/table'
import { ProductsService } from '../products.service'
import { forkJoin } from 'rxjs'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { HelpersService } from 'src/app/helpers.service'
import { SettingsService } from '../../settings/settings.service'
import { CategoriesService } from '../../categories/categories.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { LoginService } from 'src/app/login/login.service'
import { MatDialog } from '@angular/material/dialog'
import { WorksDialogComponent } from 'src/app/pages/works-dialog/works-dialog.component'
import { ScrapperComponent, ScrapperDialogResult, SelectedPost } from 'src/app/scrapper/scrapper.component'
import { ToastrService } from 'ngx-toastr'
import { ShopifyService } from 'src/app/integrations/shopify/shopify.service'
export type ProductSort = '' | 'oldest' | 'sku_asc' | 'sku_desc' | 'stock_asc' | 'stock_desc'
export interface productsTableColumns {
  Id: string
  image: string
  name: string
  sku: string
  stock: string
  price: string
  date: string
  publish: boolean
  action: string
  checked?: boolean
}
@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss']
})
export class ProductsComponent implements OnInit {
  @ViewChild('myInput', { static: false }) myInput: ElementRef | any
  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatTable)
  table!: MatTable<productsTableColumns>
  @ViewChild(MatSort, { static: true })
  matSort!: MatSort
  @ViewChild('selectAllCheckbox') selectAllCheckbox!: MatCheckbox
  @ViewChild('searchWrapper') searchWrapper!: ElementRef
  products: any[] = []
  dataSource = new MatTableDataSource<any>([])
  hasCheckedItems: boolean = false
  mobile = false
  searchText: string = ''
  displayedProducts: any[] = []
  filteredProducts: any[] = []
  showCategoryDropdown: boolean = false
  showSortDropdown: boolean = false
  // Server-side list order; '' = newest first
  sortBy: ProductSort = ''
  readonly sortOptions: { value: ProductSort; label: string }[] = [
    { value: '', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'sku_asc', label: 'SKU (A-Z)' },
    { value: 'sku_desc', label: 'SKU (Z-A)' },
    { value: 'stock_asc', label: 'Stock (Low to High)' },
    { value: 'stock_desc', label: 'Stock (High to Low)' }
  ]
  showActionsMenu: boolean = false
  categoryNameById = new Map<string, string>()
  showAssignCategoryPanel: boolean = false
  // Persists selection across pagination — displayedProducts objects are recreated on every page fetch,
  // so `checked` can't be the source of truth. Keyed by product Id.
  selectedProductIds = new Set<string>()
  selectedBulkCategoryIds: string[] = []
  selectedBulkSubCategoryIds: string[] = []
  bulkAvailableSubcategories: any[] = []

  public Math = Math;

  // Delete modal state
  showDeleteModal = false
  private productToDelete: { id: string; imageName: string; name: string } | null = null

  public productsColumns: string[] = ['image', 'name', 'sku', 'stock', 'price', 'date', 'publish', 'action']

  constructor(public loginService: LoginService,public settingsService: SettingsService, public helpers: HelpersService,public productsService: ProductsService, public builderService: BuilderService, private router: Router, private route: ActivatedRoute, private cdr: ChangeDetectorRef, private _liveAnnouncer: LiveAnnouncer,public vendor:VendorService, public categoriesService: CategoriesService, private dialog: MatDialog, private toastr: ToastrService, private shopifyService: ShopifyService) {
    this.dataSource.paginator = this.paginator
    this.loadFirstPage()
  }

  ngOnInit(): void {
    // innerWidth detects the actual viewport/iframe width; screen.width always returns the physical monitor width
    if (window.innerWidth <= 768) {
      this.mobile = true
    }

    // Check for importMode parameter (from stacks-chat product import flow)
    const importMode = localStorage.getItem('importMode')
    if (importMode) {
      // Clear the importMode after reading it
      localStorage.removeItem('importMode')

      if (importMode === 'social') {
        this.openSocialImportDialog()
      } else if (importMode === 'manual') {
        // Navigate to new product page for manual entry
        this.router.navigate(['/products/new-product'])
      }
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', (event: any) => {
      const catDropdown = document.querySelector('.category-dropdown-container')
      if (catDropdown && !catDropdown.contains(event.target)) {
        this.showCategoryDropdown = false
      }
      const sortDropdown = document.querySelector('.sort-dropdown-container')
      if (sortDropdown && !sortDropdown.contains(event.target)) {
        this.showSortDropdown = false
      }
      const assignPanel = document.querySelector('.assign-category-dropdown-container')
      if (assignPanel && !assignPanel.contains(event.target)) {
        this.showAssignCategoryPanel = false
      }
    })
  }

  howItWorksDialog = () => {
    const dialogRef = this.dialog.open(WorksDialogComponent, {
      width: '700px',
      maxWidth: '80%',
      data: { name: 'scrapper' }
    })

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed')
    })
  }

  openSocialImportDialog = () => {
    const dialogRef = this.dialog.open(ScrapperComponent, {
      width: '885px',
      maxWidth: '95vw',
      panelClass: 'social-import-dialog'
    })

    dialogRef.afterClosed().subscribe((result: ScrapperDialogResult) => {
      if (result?.action === 'import' && result.images?.length) {
        this.createProductsFromImages(result.images)
      }
    })
  }

  private createProductsFromImages(images: SelectedPost[]) {
    // Show loading indicator
    this.builderService.showUpdateProgress = true
    this.toastr.info(`Importing ${images.length} product(s)...`, 'Import Started')

    // Create product observables for all images
    const productObservables = images.map((image) => {
      const dummyValue = this.generateDummyValue()

      const newProduct = {
        productName: image.caption || `Imported Product ${dummyValue}`,
        imageUrl: image.imageUrl,
        regularPrice: dummyValue,
        productStock: dummyValue,
        productSKU: `IMP-${dummyValue}`,
        publish: false,
        productDescription: image.caption || 'Imported from social media'
      }

      return this.productsService.submitProduct(newProduct)
    })

    // Wait for all products to be created before refreshing the list
    forkJoin(productObservables).subscribe({
      next: (results) => {
        this.builderService.showUpdateProgress = false
        this.toastr.success(`Successfully imported ${results.length} product(s)`, 'Import Complete')
        results.forEach((result, i) => this.syncProductToShopify({ ...images[i], ...result }))
        // Reset pagination state and reload products to show new imports
        this.productsService.lastProductKey = null
        this.loadFirstPage()
      },
      error: (err) => {
        console.error('Failed to import products:', err)
        this.builderService.showUpdateProgress = false
        this.toastr.error('Failed to import some products', 'Import Error')
        // Still reload to show any products that were created
        this.productsService.lastProductKey = null
        this.loadFirstPage()
      }
    })
  }

  getCategoryLabels(product: any): string {
    if (!Array.isArray(product?.categoriesIds) || product.categoriesIds.length === 0) {
      return 'Uncategorized'
    }

    const names = product.categoriesIds
      .map((id: string) => this.categoryNameById.get(String(id)))
      .filter(Boolean)

    return names.length ? names.join(', ') : 'Uncategorized'
  }

  private fetchProducts(
    options: {
      reset?: boolean
      prefetchMultiplier?: number
      savedPageIndex?: number
    } = {}
  ) {
    if (this.productsService.allProductsLoaded) return
    if (this.productsService.fetchingInProgress) return

    const reset = options.reset ?? false
    const multiplier = options.prefetchMultiplier ?? 2
    const savedPageIndex = options.savedPageIndex ?? 0

    if (reset) {
      this.productsService.lastProductKey = null
      this.productsService.pageIndex = 0
      this.productsService.allProductsLoaded = false
    }

    // Fetch enough items to cover the page we want to restore
    const baseFetchSize = this.productsService.pageSize * multiplier
    const neededForRestore = (savedPageIndex + 1) * this.productsService.pageSize
    const fetchSize = Math.max(baseFetchSize, neededForRestore)

    this.productsService.getProducts('paginated', {
        limit: fetchSize,
        lastKey: this.productsService.lastProductKey ?? undefined
      })
      .subscribe(
        (res) => {
          if (!res?.items) return

          if (reset) {
            this.products = res.items
          } else {
            this.products = [...this.products, ...res.items]
          }

          this.filteredProducts = [...this.products]

          this.productsService.lastProductKey = res.lastKey

          if (reset) {
            this.initCategories()
            // Restore saved page if data covers it; pageIndex is already 0 from the reset block above
            if (this.products.length > savedPageIndex * this.productsService.pageSize) {
              this.productsService.pageIndex = savedPageIndex
            }
          }

          this.updateDisplayedProducts()
        },
        (err) => {
          console.error('Products pagination error:', err)
        }
      )
  }
  loadFirstPage() {
    this.productsService.pageIndex = 0
    this.categoriesService.clearCategoriesCache()
    this.initCategories()
    this.productsService.syncProductsCount().subscribe(count => {
    this.productsService.totalProductsCount = count || 0
    this.productsService.paginationCount = count || 0
  })
    this.loadPage(0, this.productsService.pageSize)
  }

  loadNextPagePrefetch() {
    if (this.productsService.allProductsLoaded) return
    this.fetchProducts()
  }

  loadNextPage() {
    if (this.productsService.fetchingInProgress || this.productsService.allProductsLoaded) {
      return
    }

    this.productsService.getProducts('paginated', {
        limit: this.productsService.pageSize,
        lastKey: this.productsService.lastProductKey ?? undefined
      })
      .subscribe((res) => {
        if (!res?.items?.length) return

        this.products = [...this.products, ...res.items]
        this.filteredProducts = [...this.products]
        this.updateDisplayedProducts()
      })
  }

  private initCategories() {
    this.categoriesService.loadCategories().subscribe((cats) => {
      cats.forEach((c: any) => {
        this.categoryNameById.set(c.categoryId, c.categoryName)
        c.subcategories?.forEach((s: any) => {
          this.categoryNameById.set(s.subCategoryId, s.subCategoryName)
        })
      })
    })
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
  }

  getGlobalIndex(i: number): number {
    return this.productsService.pageIndex * this.productsService.pageSize + i
  }

  // Navigation methods for pagination
  previousPage(): void {
    if (this.productsService.pageIndex > 0) {
      this.productsService.pageIndex--
      this.loadPage(this.productsService.pageIndex)
    }
  }

  nextPage(): void {
    const maxPage = Math.ceil(this.productsService.paginationCount / this.productsService.pageSize) - 1
    if (this.productsService.pageIndex < maxPage) {
      this.productsService.pageIndex++
      this.loadPage(this.productsService.pageIndex)
    }
  }

  private loadPage(page: number, pageSize = this.productsService.pageSize): void {
    const categoryId = this.categoriesService.selectedSubCategoryFilter ||
      (this.categoriesService.selectedCategory !== 'all' ? this.categoriesService.selectedCategory : null)
    const searchText = this.searchText.trim() || undefined

    const sortBy = this.sortBy || undefined

    const fetch$ = categoryId
      ? this.productsService.fetchPageByCategory(categoryId, page, pageSize, searchText, sortBy)
      : this.productsService.fetchPage(page, pageSize, searchText, sortBy)

    fetch$.subscribe({
      next: products => {
        this.products = products
        this.reapplyFilters()
      },
      error: err => console.error('[loadPage] fetch failed:', err)
    })
  }

goToPage(page: number): void {
    const maxPage = Math.ceil(this.productsService.paginationCount / this.productsService.pageSize) - 1
    if (page >= 0 && page <= maxPage) {
      this.productsService.pageIndex = page
      this.loadPage(page)
    }
  }

  toggleCategoryDropdown(event: Event) {
    event.stopPropagation();
    this.showCategoryDropdown = !this.showCategoryDropdown;

    if (this.showCategoryDropdown) {
      setTimeout(() => {
        const wrapper = this.searchWrapper?.nativeElement;
        if (wrapper && this.mobile) {
          // Scroll to the entire search wrapper (input + padding)
          const y = wrapper.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        this.myInput?.nativeElement?.focus();
      }, 100);
    }

  }
  toggleSortDropdown(event: Event) {
    event.stopPropagation()
    this.showSortDropdown = !this.showSortDropdown
  }

  getSortLabel(): string {
    return this.sortOptions.find((o) => o.value === this.sortBy)?.label ?? 'Newest First'
  }

  /** Changes the list order and reloads from page 0 (sorting is done server-side). */
  selectSort(value: ProductSort) {
    this.showSortDropdown = false
    if (this.sortBy === value) return
    this.sortBy = value
    this.productsService.pageIndex = 0
    if (this.paginator) this.paginator.pageIndex = 0
    this.loadPage(0)
  }

  // Focus on search input when dropdown opens
  filterCategories() {
    const search = this.categoriesService.categorySearch.trim().toLowerCase()

    if (!search) {
      this.categoriesService.filteredCategories = this.categoriesService.allCategories
      return
    }

    this.categoriesService.filteredCategories = this.categoriesService.allCategories.filter((cat: any) => {
      if (cat.categoryName.toLowerCase().includes(search)) return true
      return cat.subcategories?.some((sub: any) => sub.subcategory.toLowerCase().includes(search))
    })
  }

  // NEW: Select Category from dropdown
  selectCategory(id: string, name: string, isSubcategory = false) {
    this.categoriesService.selectedCategory = isSubcategory ? '' : id
    this.categoriesService.selectedSubCategoryFilter = isSubcategory ? id : ''
    this.categoriesService.selectedCategoryName = name
    this.showCategoryDropdown = false
    this.categoriesService.categorySearch = ''
    this.categoriesService.filteredCategories = this.categoriesService.allCategories

    const categoryId = this.categoriesService.selectedSubCategoryFilter ||
      (this.categoriesService.selectedCategory !== 'all' ? this.categoriesService.selectedCategory : null)

    if (categoryId) {
      this.productsService.getProductsCountByCategory(categoryId).subscribe(count => {
        this.productsService.paginationCount = count
      })
    } else {
      this.productsService.paginationCount = this.productsService.totalProductsCount
    }

    this.productsService.pageIndex = 0
    this.loadPage(0, this.productsService.pageSize)
  }

  filterByCategory() {
    if (this.categoriesService.selectedCategory === 'all' && !this.categoriesService.selectedSubCategoryFilter) {
      this.filteredProducts = [...this.products]
    } else if (this.categoriesService.selectedSubCategoryFilter) {
      this.filteredProducts = this.products.filter((p: any) => Array.isArray(p.subCategoriesIds) && p.subCategoriesIds.includes(this.categoriesService.selectedSubCategoryFilter))
    } else {
      this.filteredProducts = this.products.filter((p: any) => {
        if (!p.categoriesIds) return false
        return p.categoriesIds.includes(this.categoriesService.selectedCategory)
      })
    }

    if (this.searchText.trim()) {
      const filterValue = this.searchText.trim().toLowerCase()
      this.filteredProducts = this.filteredProducts.filter((data) => {
        const name = (data.productName || '').toLowerCase()
        const sku = (data.productSKU || '').toLowerCase()
        const date = data.date || ''
        return name.includes(filterValue) || sku.includes(filterValue) || date.includes(filterValue)
      })
    }

    this.resetPaging()
    this.updateDisplayedProducts()
  }


  /**
   * Fetch all products and load the first page initially
   */
  getAllData() {
    this.productsService.getProducts().subscribe((response: any) => {
      if (response) {
        this.products = Object.values(response)
        this.filteredProducts = [...this.products]
        this.categoriesService.getCategories().subscribe(() => {
          this.categoriesService.filteredCategories = this.categoriesService.allCategories
        })
        this.resetPaging()
        this.updateDisplayedProducts()
        this.filterByCategory()
      }
    })
  }

  // Get current product count
  getCurrentProductCount(): number {
    return this.productsService.totalProductsCount
  }

  resetPaging() {
    this.productsService.pageIndex = 0
    if (this.paginator) {
      this.paginator.pageIndex = 0
    }
  }

  /**
   * Syncs each product's `checked` flag from the persistent selectedProductIds set,
   * so selection survives pagination instead of being wiped on every page fetch.
   */
  private syncCheckedFromSelection() {
    this.filteredProducts.forEach((p) => (p.checked = this.selectedProductIds.has(String(p.Id))))
    this.hasCheckedItems = this.selectedProductIds.size > 0
  }

  private clearSelection() {
    this.selectedProductIds.clear()
    this.filteredProducts.forEach((p) => (p.checked = false))
    this.hasCheckedItems = false
  }

  /**
   * Updates the "Select All" checkbox state based on current page items.
   */
  updateSelectAllState() {
    if (!this.selectAllCheckbox || !this.displayedProducts.length) return
    const allChecked = this.displayedProducts.every((item) => item.checked)
    const someChecked = this.displayedProducts.some((item) => item.checked)

    this.selectAllCheckbox.checked = allChecked
    this.selectAllCheckbox.indeterminate = someChecked && !allChecked
  }

  /**
   * Updates the displayed products based on pagination.
   * this.filteredProducts is already one page fetched from Supabase — no slicing needed.
   */
  updateDisplayedProducts() {
    this.displayedProducts = [...this.filteredProducts]

    this.syncCheckedFromSelection()
    // Update "Select All" state for the new page
    this.updateSelectAllState()
  }

  /**
   * Handles page change event — fetches the new page from Supabase.
   */
  onPageChange(event: PageEvent) {
    this.productsService.pageSize = event.pageSize
    this.productsService.pageIndex = event.pageIndex
    this.loadPage(event.pageIndex, event.pageSize)
  }

  /**
   * Handles manual page size change from the custom UI.
   */
  changePageSize(newSize: string | number) {
    const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize
    const event: PageEvent = {
      pageIndex: 0,
      pageSize: size,
      length: this.productsService.paginationCount
    }

    // Sync with the hidden mat-paginator
    if (this.paginator) {
      this.paginator.pageSize = size
      this.paginator.pageIndex = 0
    }

    this.onPageChange(event)
  }

  /**
   * Select/unselect all checkboxes in the current page.
   */
  toggleSelectAllItems() {
    const allItemsChecked = this.displayedProducts.every((item) => item.checked)
    const select = !allItemsChecked
    this.displayedProducts.forEach((item) => {
      item.checked = select
      if (select) {
        this.selectedProductIds.add(String(item.Id))
      } else {
        this.selectedProductIds.delete(String(item.Id))
      }
    })
    this.hasCheckedItems = this.selectedProductIds.size > 0
  }

  /**
   * Check if at least one item is selected.
   */
  onCheckboxItem() {
    this.displayedProducts.forEach((item) => {
      if (item.checked) {
        this.selectedProductIds.add(String(item.Id))
      } else {
        this.selectedProductIds.delete(String(item.Id))
      }
    })
    this.hasCheckedItems = this.selectedProductIds.size > 0
    this.updateSelectAllState()
    this.cdr.detectChanges()
  }

  /**
   * Deletes selected products
   */
  deleteCheckedItems() {
    const checkedItems = this.displayedProducts.filter((item) => item.checked)
    if (checkedItems.length === 0) {
      console.log('No items selected for deletion.')
      return
    }

    const itemNames = checkedItems.map((item) => item.productName).join(', ')

    const confirmation = window.confirm(`Are you sure you want to delete the selected items?\nItems: ${itemNames}`)
    if (confirmation) {
      this.builderService.showUpdateProgress = true
      for (let index = 0; index < checkedItems.length; index++) {
        const element = checkedItems[index]
        // imageUrl is the full image array — deleteImage() takes a single path
        this.deleteProduct(element.Id, this.helpers.getArrayValue(element.imageUrl), false)
        this.selectedProductIds.delete(String(element.Id))
      }
      this.filteredProducts = this.products.filter((item) => !item.checked)
      this.updateDisplayedProducts()
      this.selectAllCheckbox.checked = false
      this.hasCheckedItems = this.selectedProductIds.size > 0
    } else {
      console.log('Deletion canceled.')
    }
  }

  uncheckAllCheckboxes() {
    this.clearSelection()
    this.selectAllCheckbox.checked = false
    this.cdr.detectChanges()
  }

  toggleAssignCategoryPanel(event: Event) {
    event.stopPropagation()
    this.showAssignCategoryPanel = !this.showAssignCategoryPanel
  }

  onBulkCategoryChange() {
    this.selectedBulkSubCategoryIds = []
    const selectedCats = this.categoriesService.allCategories.filter((c: any) => this.selectedBulkCategoryIds.includes(c.categoryId))
    this.bulkAvailableSubcategories = selectedCats.reduce((acc: any[], cat: any) => [...acc, ...(cat.subcategories || [])], [])
  }

  toggleBulkCategory(categoryId: string) {
    const index = this.selectedBulkCategoryIds.indexOf(categoryId)
    if (index > -1) {
      this.selectedBulkCategoryIds.splice(index, 1)
    } else {
      this.selectedBulkCategoryIds.push(categoryId)
    }
    this.onBulkCategoryChange()
  }

  toggleBulkSubCategory(subCategoryId: string) {
    const index = this.selectedBulkSubCategoryIds.indexOf(subCategoryId)
    if (index > -1) {
      this.selectedBulkSubCategoryIds.splice(index, 1)
    } else {
      this.selectedBulkSubCategoryIds.push(subCategoryId)
    }
  }

  bulkAssignCategory = () => {
    const checkedItems = this.displayedProducts.filter((item) => item.checked)
    if (this.selectedBulkCategoryIds.length === 0 || checkedItems.length === 0) return

    this.builderService.showUpdateProgress = true
    const observables = checkedItems.map((product) => {
      const categoriesIds = Array.isArray(product.categoriesIds) ? [...product.categoriesIds] : []
      this.selectedBulkCategoryIds.forEach((id) => {
        if (!categoriesIds.includes(id)) {
          categoriesIds.push(id)
        }
      })

      const subCategoriesIds = Array.isArray(product.subCategoriesIds) ? [...product.subCategoriesIds] : []
      this.selectedBulkSubCategoryIds.forEach((id) => {
        if (!subCategoriesIds.includes(id)) {
          subCategoriesIds.push(id)
        }
      })

      const { checked, ...productWithoutChecked } = product
      const updatedProduct = { ...productWithoutChecked, categoriesIds, subCategoriesIds }

      // Update local product object so UI reflects changes immediately
      product.categoriesIds = categoriesIds
      product.subCategoriesIds = subCategoriesIds

      return this.productsService.submitProduct(updatedProduct, product.Id)
    })

    forkJoin(observables).subscribe({
      next: () => {
        this.builderService.showUpdateProgress = false
        this.showAssignCategoryPanel = false
        this.selectedBulkCategoryIds = []
        this.selectedBulkSubCategoryIds = []
        this.bulkAvailableSubcategories = []
        this.uncheckAllCheckboxes()
        this.toastr.success('Categories assigned successfully')
        this.cdr.detectChanges()
      },
      error: (err) => {
        console.error('Bulk assign category error:', err)
        this.builderService.showUpdateProgress = false
        this.toastr.error('Failed to assign categories')
      }
    })
  }
  openDeleteModal(id: string, imageName: string, name: string): void {
    this.productToDelete = { id, imageName, name }
    this.showDeleteModal = true
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false
    this.productToDelete = null
  }

  get productToDeleteName(): string {
    return this.productToDelete?.name ?? ''
  }

  confirmDeleteProduct(): void {
    if (!this.productToDelete) return
    const { id, imageName } = this.productToDelete
    this.deleteProduct(id, imageName, false)
    this.closeDeleteModal()
  }

  deleteProduct = (Id: string, imageName: string, singleItem = true) => {
    if (singleItem) return // single-item deletion goes through openDeleteModal → confirmDeleteProduct

    this.productsService.removeProduct(Id).subscribe(() => {
    const product = this.products.find(p => p.Id === Id)
      const documentUrls = product?.productDocumentUrls || []

      const deleteObservables = []

      if (imageName) {
        deleteObservables.push(this.builderService.deleteImage(imageName))
      }

      if (documentUrls.length > 0) {
        for (const doc of documentUrls) {
          deleteObservables.push(this.builderService.deleteDocument(doc.url))
        }
      }

      if (deleteObservables.length > 0) {
        //  Using forkJoin here instead of a normal loop:
        // - forkJoin waits for all async delete calls to finish before continuing
        // - It gives you success/error feedback for the whole batch
        // - A normal loop (with .subscribe inside) would trigger parallel deletes,
        //   but you wouldn’t know when all are done, and error handling would be messy.
        forkJoin(deleteObservables).subscribe({
          next: () => {
            this.loadFirstPage()
            this.updateDisplayedProducts()
            this.builderService.showUpdateProgress = false
          },
          error: (err) => {
            console.error('Error deleting files:', err)
            this.loadFirstPage()
            this.updateDisplayedProducts()
            this.builderService.showUpdateProgress = false
          }
        })
      } else {
        this.loadFirstPage()
        this.updateDisplayedProducts()
        this.builderService.showUpdateProgress = false
      }
    })
  }

  editProduct(Id: string) {
    this.router.navigate([`/products/new-product/${Id}`], { relativeTo: this.route })
  }
  /** Announce the change in sort state for assistive technology. */
  announceSortChange(sortState: Sort) {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`)
    } else {
      this._liveAnnouncer.announce('Sorting cleared')
    }
  }

  private searchDebounce: any

  applyFilter() {
    clearTimeout(this.searchDebounce)
    this.searchDebounce = setTimeout(() => {
      this.productsService.pageIndex = 0
      if (this.paginator) this.paginator.pageIndex = 0

      const searchText = this.searchText.trim() || undefined
      const categoryId = this.categoriesService.selectedSubCategoryFilter ||
        (this.categoriesService.selectedCategory !== 'all' ? this.categoriesService.selectedCategory : null)

      if (categoryId) {
        this.productsService.getProductsCountByCategory(categoryId, searchText).subscribe(count => {
          this.productsService.paginationCount = count
        })
      } else if (searchText) {
        this.productsService.syncProductsCount(searchText).subscribe(count => {
          this.productsService.paginationCount = count
        })
      } else {
        this.productsService.paginationCount = this.productsService.totalProductsCount
      }

      this.loadPage(0)
    }, 300)
  }

  /**
   * Change the product visibility to hide or show the product by sending the status to the db.
   */
  onToggleChange(product: productsTableColumns, event: any) {
    const checked = event.target ? event.target.checked : event.checked
    product.publish = checked
    this.productsService.setProductStatus(product.Id, checked).subscribe((response) => {})
  }
  /**
   * Server handles both category and text filtering — just copy the page as-is.
   */
  private reapplyFilters(): void {
    const savedPageIndex = this.productsService.pageIndex
    this.filteredProducts = [...this.products]
    this.productsService.pageIndex = savedPageIndex
    this.updateDisplayedProducts()
  }
  isExporting = false

  /**
   * Exports products to CSV.
   * When products are selected, only those are exported; otherwise all products are exported.
   */
  exportAllProductData(): void {
    if (this.isExporting) return
    this.isExporting = true

    const selectedIds = this.getSelectedProductIds()

    this.productsService.getAllProductsForExport().subscribe({
      next: (src) => this.generateCsvExport(this.filterProductsForExport(src, selectedIds)),
      error: (err) => {
        console.error('Failed to fetch products for export:', err)
        this.isExporting = false
      }
    })
  }

  /**
   * Ids of the currently checked products, empty when nothing is selected.
   */
  private getSelectedProductIds(): string[] {
    return this.displayedProducts.filter((item) => item.checked).map((item) => String(item.Id))
  }

  /**
   * Keeps only the selected products; returns the full list when nothing is selected.
   */
  private filterProductsForExport(src: any[], selectedIds: string[]): any[] {
    if (!Array.isArray(src) || selectedIds.length === 0) return src
    const idSet = new Set(selectedIds)
    return src.filter((product) => idSet.has(String(product?.Id)))
  }

  private generateCsvExport(src: any[]): void {
    this.isExporting = false

    if (!src || !Array.isArray(src) || src.length === 0) {
      console.warn('No products to export.')
      return
    }

    const headers = [
      'productId',
      'productName',
      'imageUrl',
      'categoriesIds',
      'subCategoriesIds',
      'date',
      'image',
      'productDescription',
      'productSKU',
      'productStock',
      'productFormat',
      'publish',
      'regularPrice',
      'salePrice',
      'estimatedDeliveryTime',
      'estimatedDeliveryUnit',
      'userType',
      'location',
      'sizeChartUrl',
      'attribute_ABC',
      'attribute_Points',
      'attribute_Type',
      'variation_regularPrice',
      'variation_salePrice',
      'variation_sku',
      'variation_stock',
      'variation_values',
      'imageSizeType',
      'imageWidth',
      'imageHeight'
    ]

    const csvRows: string[] = []
    csvRows.push(headers.join(','))

    for (const product of src) {
      // helpers
      const joinVals = (v: any): string => {
        if (Array.isArray(v)) {
          return v.join(', ')
        } else if (v && typeof v === 'object') {
          return Object.values(v).join(', ')
        } else if (v === undefined || v === null) {
          return ''
        } else {
          return String(v)
        }
      }

      const firstVar = Array.isArray(product?.productVariations) && product.productVariations.length > 0 ? product.productVariations[0] : null

      const getAttr = (attrName: string): string => {
        const attrs = Array.isArray(product?.productAttributes) ? product.productAttributes : []
        const found = attrs.find((a: any) => (a?.name || '').toLowerCase() === attrName.toLowerCase())
        if (!found) {
          return ''
        } else {
          const val = found.value ?? found.values
          if (Array.isArray(val)) {
            return val.join(' / ')
          } else if (val === undefined || val === null) {
            return ''
          } else {
            return String(val)
          }
        }
      }

      const dataByHeader: Record<string, any> = {
        productId: product?.Id ?? '',
        productName: product?.productName ?? '',
        imageUrl: joinVals(product?.imageUrl),
        categoriesIds: joinVals(product?.categoriesIds),
        subCategoriesIds: joinVals(product?.subCategoriesIds),
        date: product?.date ?? '',
        image: firstVar?.image ?? '',
        productDescription: product?.productDescription ?? '',
        productSKU: product?.productSKU ?? '',
        productStock: product?.productStock ?? '',
        productFormat: product?.productFormat ?? 'physical',
        publish: product?.publish ?? '',
        regularPrice: product?.regularPrice ?? '',
        salePrice: product?.salePrice ?? '',
        estimatedDeliveryTime: product?.estimatedDeliveryTime ?? '',
        estimatedDeliveryUnit: product?.estimatedDeliveryUnit ?? '',
        userType: product?.userType ?? 'customer',
        location: product?.location ?? '',
        sizeChartUrl: product?.sizeChartUrl ?? '',
        imageSizeType: product?.imageSizeType ? product.imageSizeType : 'auto',
        imageWidth: product?.imageWidth ?? '',
        imageHeight: product?.imageHeight ?? '',
        // attributes
        attribute_ABC: getAttr('ABC'),
        attribute_Points: getAttr('Points'),
        attribute_Type: getAttr('Type'),

        // first variation flatten
        variation_regularPrice: firstVar?.regularPrice ?? '',
        variation_salePrice: firstVar?.salePrice ?? '',
        variation_sku: firstVar?.sku ?? '',
        variation_stock: firstVar?.stock ?? firstVar?.quantity ?? '',
        variation_values: (() => {
          const vv = firstVar?.values ?? firstVar?.selectedValues ?? firstVar?.selectedVariationValues
          if (Array.isArray(vv)) {
            return vv.join(' / ')
          } else if (vv && typeof vv === 'object') {
            return Object.values(vv).join(' / ')
          } else {
            return vv ?? ''
          }
        })()
      }

      const row = headers.map((h) => {
        let v = dataByHeader[h]
        if (v === undefined || v === null) {
          v = ''
        } else if (typeof v === 'object') {
          try {
            v = JSON.stringify(v)
          } catch {
            v = String(v)
          }
        } else {
          v = String(v)
        }
        return `"${v.replace(/"/g, '""')}"`
      })

      csvRows.push(row.join(','))
    }

    const csvContent = '﻿' + csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'stacks-products.csv'
    a.click()
    window.URL.revokeObjectURL(url)

    this.uncheckAllCheckboxes()
  }

  syncProductToShopify(product: any): void {
    this.shopifyService.pushProduct(product).subscribe({
      error: (err: any) => console.error('Shopify sync failed:', err)
    })
  }

  private generateDummyValue(): number {
    return Math.floor(Math.random() * 991) + 10
  }

  /**
   * Gets the display price for a product.
   * For variable products, returns price range (min - max).
   */
  getProductPrice(product: any): string {
    if (product.productType === 'variable' && product.productVariations?.length > 0) {
      const prices = product.productVariations
        .map((v: any) => parseFloat(v.salePrice || v.regularPrice))
        .filter((p: number) => !isNaN(p))

      if (prices.length === 0) return ''

      const minPrice = Math.min(...prices)
      const maxPrice = Math.max(...prices)

      if (minPrice === maxPrice) {
        return minPrice.toString()
      }
      return `${minPrice} / ${maxPrice}`
    }
    return product.salePrice || product.regularPrice || ''
  }

  /**
   * Gets the display SKU for a product.
   * For variable products, returns the first variation's SKU.
   */
  getProductSKU(product: any): string {
    if (product.productType === 'variable' && product.productVariations?.length > 0) {
      const firstVar = product.productVariations[0]
      return firstVar.sku || '_'
    }
    return product.productSKU || '_'
  }
}
